import { useTranslation } from 'react-i18next';

import { DateTools, Table, TTableColumn, TextSkeleton } from '@treezor/design-system';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { IScaWallet } from '@/store/models/sca.model';

import { SCAStatus } from './SCAStatus';
import { SCAActions } from './SCAActions';

interface ISCATableProps {
  list: IScaWallet[];
  isLoading?: boolean;
}

export function SCATable({ list = [], isLoading }: ISCATableProps) {
  const { t } = useTranslation();

  const rows = isLoading
    ? new Array(5).fill({})
    : [
        ...list.map((device) => ({
          id: device.scaWalletId,
          creationDate: device.creationDate || '-',
          tag: device.scaWalletTag || '-',
          status: device,
          device:
            device.settingsProfile === 'default'
              ? [device.mobileWallet?.os, device.mobileWallet?.brand, device.mobileWallet?.productModel]
                  .filter(Boolean)
                  .join(' | ') || '-'
              : 'Web',
          action: device,
        })),
      ];

  type TData = (typeof rows)[number];

  const columns: TTableColumn<TData>[] = [
    {
      name: 'creationDate',
      field: 'creationDate',
      isSortable: true,
      label: t('pages.currentProfiles.profile.sca.creationDate'),
      cellRenderer: (item) =>
        isLoading ? <TextSkeleton width="180px" margin="5px" /> : DateTools.format(item.creationDate),
    },
    {
      name: 'status',
      field: 'status',
      isSortable: true,
      label: t('pages.currentProfiles.profile.sca.status'),
      cellRenderer: (item) =>
        isLoading ? <TextSkeleton width="150px" margin="0" /> : <SCAStatus scaWallet={item.status} />,
    },
    {
      name: 'device',
      field: 'device',
      isSortable: true,
      label: t('pages.currentProfiles.profile.sca.device'),
    },
    {
      name: 'tag',
      field: 'tag',
      isSortable: true,
      label: t('pages.currentProfiles.profile.sca.scaWalletTag'),
    },
    {
      name: 'action',
      field: 'action',
      label: '',
      cellRenderer: (item) =>
        isLoading ? <TextSkeleton width="120px" margin="0" /> : <SCAActions scaWallet={item.action} />,
    },
  ];

  return (
    <Table
      columns={columns}
      rows={rows}
      emptyComponent={
        <EmptyState title={t('pages.currentProfiles.profile.sca.noDevice')} icon="search_v1" size="m" className="p-9" />
      }
    />
  );
}
